// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { Modal, StatusBanner, Spinner } from '@loa/ui-components';
import { tagsApi } from '../../api/tagsApi';
import { fundsApi } from '../../api/fundsApi';
import type { TagDTO } from '../../types/tag';

export interface TagDeleteModalProps {
  open: boolean;
  tag: TagDTO | null;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Delete confirm showing usage counts (Tags PRD §9 / FR-22). Merges the
 * resource-server per-entity-type usage with funds-service's fund
 * assignment count (FR-15) so the advisor sees the FULL blast radius,
 * including fund assignments that will disappear via the `tag.deleted`
 * propagation event shortly after.
 */
export function TagDeleteModal({ open, tag, onClose, onSuccess }: Readonly<TagDeleteModalProps>) {
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [countsByEntityType, setCountsByEntityType] = useState<Record<string, number>>({});
  const [fundCount, setFundCount] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !tag) return;
    setError(null);
    setLoadingUsage(true);
    Promise.all([
      tagsApi.usage(tag.id),
      fundsApi.getFundTagUsage([tag.id]).catch(() => []),
    ])
      .then(([usage, fundUsage]) => {
        setCountsByEntityType(usage.countsByEntityType);
        setFundCount(fundUsage.find((u) => u.tagId === tag.id)?.count ?? 0);
      })
      .catch(() => {
        setCountsByEntityType({});
        setFundCount(null);
      })
      .finally(() => setLoadingUsage(false));
  }, [open, tag]);

  const handleClose = () => {
    if (deleting) return;
    onClose();
  };

  const handleDelete = async () => {
    if (!tag) return;
    setDeleting(true);
    setError(null);
    try {
      await tagsApi.remove(tag.id);
      onSuccess();
      onClose();
    } catch (err) {
      const apiError = err as { response?: { data?: { message?: string } } };
      setError(apiError.response?.data?.message || 'Failed to delete tag. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  if (!tag) return null;

  const entityRows = Object.entries(countsByEntityType).filter(([, count]) => count > 0);
  const totalUsage = entityRows.reduce((sum, [, count]) => sum + count, 0) + (fundCount ?? 0);

  return (
    <Modal open={open} onClose={handleClose} size="small" variant="warning">
      <Modal.Header icon="trash" title={`Delete "${tag.name}"`} onClose={handleClose} />
      <Modal.Content>
        {error && (
          <StatusBanner variant="error" message={error} visible position="inline" dismissible={false} />
        )}
        {loadingUsage ? (
          <Spinner size="sm" display="inline" />
        ) : (
          <div className="flex flex-col gap-[8px]">
            <p className="text-[14px] text-[#525252]">
              {totalUsage > 0
                ? `This will remove ${totalUsage} assignment${totalUsage === 1 ? '' : 's'}:`
                : 'This tag has no assignments.'}
            </p>
            {entityRows.length > 0 && (
              <ul className="text-[14px] text-[#141414] pl-[16px] list-disc">
                {entityRows.map(([type, count]) => (
                  <li key={type}>{count} {type.toLowerCase()}{count === 1 ? '' : 's'}</li>
                ))}
              </ul>
            )}
            {fundCount != null && fundCount > 0 && (
              <ul className="text-[14px] text-[#141414] pl-[16px] list-disc">
                <li>{fundCount} fund{fundCount === 1 ? '' : 's'}</li>
              </ul>
            )}
          </div>
        )}
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{ label: 'Cancel', onClick: handleClose, disabled: deleting, colour: 'red' }}
        primaryAction={{
          label: 'Delete tag',
          onClick: () => { void handleDelete(); },
          loading: deleting,
          disabled: deleting,
          colour: 'red',
        }}
      />
    </Modal>
  );
}
